import { API_ROOT_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';
import type {
  ActorWithRoles,
  CreatePermissionSetBody,
  MyPermissions,
  PermissionBlueprint,
  RoleDetail,
  RoleView,
} from './types';

const ROOT = '/api/permissions';
const TENANT_HEADER = 'x-retraced-tenant-id';

export const fetchMyPermissions = () =>
  apiFetch<{ data: MyPermissions }>(`${ROOT}/me`, {}, API_ROOT_URL);

/**
 * A tenant's roles, scoped via `x-retraced-tenant-id` rather than the
 * caller's own company context — for Retraced-staff management screens that
 * pick a target company (`app/management`). The backend's `tenantOf` honours
 * the header only for Retraced staff, or when it names the caller's own
 * company; any other caller gets 403. The permission guard checks the caller's
 * own company, never the header's, so this is not a way for a company user to
 * reach another company's roles.
 */
export const fetchRolesForTenant = (tenantId: string) =>
  apiFetch<{ data: RoleView[] }>(
    `${ROOT}/roles`,
    { headers: { [TENANT_HEADER]: tenantId } },
    API_ROOT_URL,
  );

export const fetchBlueprint = () =>
  apiFetch<{ data: PermissionBlueprint }>(
    `${ROOT}/catalog/blueprint`,
    {},
    API_ROOT_URL,
  );

export const fetchRoles = () =>
  apiFetch<{ data: RoleView[] }>(`${ROOT}/roles`, {}, API_ROOT_URL);

export const fetchRole = (key: string) =>
  apiFetch<{ data: RoleDetail }>(
    `${ROOT}/roles/${encodeURIComponent(key)}`,
    {},
    API_ROOT_URL,
  );

export const fetchActors = () =>
  apiFetch<{ data: ActorWithRoles[] }>(`${ROOT}/actors`, {}, API_ROOT_URL);

export const createRole = (body: CreatePermissionSetBody) =>
  apiFetch<{ data: RoleView }>(
    `${ROOT}/roles`,
    { method: 'POST', body: JSON.stringify(body) },
    API_ROOT_URL,
  );

export const updateRole = (
  key: string,
  body: { name?: string; description?: string | null },
) =>
  apiFetch<{ data: RoleView }>(
    `${ROOT}/roles/${encodeURIComponent(key)}`,
    { method: 'PATCH', body: JSON.stringify(body) },
    API_ROOT_URL,
  );

export const deleteRole = (key: string) =>
  apiFetch<{ data: { ok: boolean } }>(
    `${ROOT}/roles/${encodeURIComponent(key)}`,
    { method: 'DELETE' },
    API_ROOT_URL,
  );

export const setRolePermissions = (key: string, permissions: string[]) =>
  apiFetch<{ data: { ok: boolean } }>(
    `${ROOT}/roles/${encodeURIComponent(key)}/permissions`,
    { method: 'PUT', body: JSON.stringify({ permissions }) },
    API_ROOT_URL,
  );

export const assignRoleMember = (
  key: string,
  actorType: string,
  actorId: string,
) =>
  apiFetch<{ data: { ok: boolean } }>(
    `${ROOT}/roles/${encodeURIComponent(key)}/members`,
    { method: 'POST', body: JSON.stringify({ actorType, actorId }) },
    API_ROOT_URL,
  );

export const removeRoleMember = (
  key: string,
  actorType: string,
  actorId: string,
) =>
  apiFetch<{ data: { ok: boolean } }>(
    `${ROOT}/roles/${encodeURIComponent(key)}/members/${encodeURIComponent(actorType)}/${encodeURIComponent(actorId)}`,
    { method: 'DELETE' },
    API_ROOT_URL,
  );
